export const MAX_CATALOG_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;
export const DEFAULT_CATALOG_IMPORT_BATCH_SIZE = 500;

/**
 * The largest video the catalog will take end to end.
 *
 * It is the importer's download budget and the metadata extractor's ceiling,
 * so a video above it imports but never gets duration, aspect ratio, or a
 * transcript. The upload form rejects it up front rather than accepting a
 * video that silently arrives without metadata.
 */
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;

/**
 * Default maximum size for a single image (20MB). Also the threshold above
 * which JPEG/PNG/WebP originals get a downscaled standard copy for analysis.
 */
export const DEFAULT_MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

export const STANDARD_COPY_MAX_IMAGE_SIZE_BYTES = 200 * 1024 * 1024;

const MAX_IMAGE_SIZE_BY_MIME_TYPE: ReadonlyMap<string, number> = new Map([
  ["image/tiff", STANDARD_COPY_MAX_IMAGE_SIZE_BYTES],
  ["image/avif", STANDARD_COPY_MAX_IMAGE_SIZE_BYTES],
  // Oversized JPEG/PNG/WebP keep their full-size original for serving and
  // get a downscaled standard copy for LLM analysis.
  ["image/jpeg", STANDARD_COPY_MAX_IMAGE_SIZE_BYTES],
  ["image/png", STANDARD_COPY_MAX_IMAGE_SIZE_BYTES],
  ["image/webp", STANDARD_COPY_MAX_IMAGE_SIZE_BYTES],
  // GIFs use the default limit: animated GIFs can't be downscaled to a compact
  // analysis copy cheaply.
]);

/**
 * Returns the max allowed import size for a given MIME type.
 * Formats that get a standard copy for analysis (TIFF, AVIF, and the
 * downscalable raster formats) allow much larger originals.
 */
export function getMaxImageSize(mimeType: string | null | undefined): number {
  if (!mimeType) return DEFAULT_MAX_IMAGE_SIZE_BYTES;
  return (
    MAX_IMAGE_SIZE_BY_MIME_TYPE.get(mimeType.toLowerCase()) ??
    DEFAULT_MAX_IMAGE_SIZE_BYTES
  );
}

const ASSET_FILE_UPLOAD_PATH_PREFIX = "ad-product-catalog/asset-uploads";

/** Where single asset files are uploaded before an import picks them up. */
export function getAssetFileUploadPrefix(workspaceId: number): string {
  return `${workspaceId}/${ASSET_FILE_UPLOAD_PATH_PREFIX}/`;
}

const STAGED_UPLOAD_REFERENCE_PREFIX = "staged-upload:";

/** A stable reference to an original uploaded through the library's asset upload route. */
export function stagedUploadReference(key: string): string {
  return `${STAGED_UPLOAD_REFERENCE_PREFIX}${key}`;
}

export function parseStagedUploadReference(reference: string): string | null {
  return reference.startsWith(STAGED_UPLOAD_REFERENCE_PREFIX)
    ? reference.slice(STAGED_UPLOAD_REFERENCE_PREFIX.length)
    : null;
}
